/* eslint-disable @typescript-eslint/no-empty-function */
import { Lightning, Colors, Router, AppData } from '@lightningjs/sdk';
import Request from '@lightningjs/sdk/src/Router/model/Request';
import { getFontFaceFromStyle } from 'support/textUtils';
import Button from 'components/common/buttons/Button';
import { translate } from 'support/translate';
import { closeApp } from 'support/appUtils';
import { constants } from 'aliases';
import { getFocusDepth } from 'support/generalUtils';
import { RoutePath } from 'config/routes';
import { navigateHome } from 'support/routerUtils';
import TextLabel from 'components/common/TextLabel';

const TITLE_MARGIN_BOTTOM = 24;
const DESCRIPTION_MARGIN_BOTTOM = 90;

const TITLE_FONT_SIZE = 63;
const DESCRIPTION_FONT_SIZE = 42;

const MIN_BUTTON_WIDTH = 351;
const BUTTON_PADDING = 18;
const BUTTON_PADDINGH = 84;

export enum ErrorCause {
  CHANNEL = 'channel',
  CONFIGURATION = 'configuration',
  PLAYBACK = 'playback',
  LIVE_PLAYBACK = 'livePlayback',
  LIVE_PAGE = 'livePage',
  SPLASH = 'splash',
  NAVIGATION = 'navigation',
  UNKNOWN = 'unknown',
  UNAVAILABLE = 'unavailable',

  // Live Ended
  LIVE_ENDED_ON_EXIT_PLAYER = 'liveEndedOnExitPlayer',
  LIVE_ENDED_ON_DEEPLINK = 'liveEndedOnDeeplink',
}

export type ErrorModalArgs = {
  cause?: ErrorCause;
  navigationRequest?: Request;
};

export interface ErrorModalTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Title: typeof TextLabel;
  Description: typeof TextLabel;
  Button: typeof Button;
}

export default class ErrorModal
  extends Lightning.Component<ErrorModalTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<ErrorModalTemplateSpec>
{
  private _Title = this.getByRef('Title')!;
  private _Description = this.getByRef('Description')!;
  private _Button = this.getByRef('Button')!;

  // Internal properties
  private static _cause: ErrorCause | undefined;
  private static _navigationRequest: Request | undefined;
  private static _retryCount = 0;

  get announce() {
    const title = this._Title.text?.text ?? '';
    const description = this._Description.text?.text ?? '';
    return [title, description];
  }

  static override _template(): Lightning.Component.Template<ErrorModalTemplateSpec> {
    return {
      alpha: 0,
      rect: true,
      w: 1920,
      h: 1080,
      color: Colors('modalBackground').get(),
      flex: {
        direction: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
      Title: {
        type: TextLabel,
        flexItem: { marginBottom: TITLE_MARGIN_BOTTOM },
        text: {
          fontSize: TITLE_FONT_SIZE,
          fontFace: getFontFaceFromStyle('bold'),
        },
      },
      Description: {
        type: TextLabel,
        flexItem: { marginBottom: DESCRIPTION_MARGIN_BOTTOM },
        text: { fontSize: DESCRIPTION_FONT_SIZE },
      },
      Button: {
        type: Button,
        minWidth: MIN_BUTTON_WIDTH,
        padding: BUTTON_PADDING,
        paddingH: BUTTON_PADDINGH,
        action: '$onButtonClicked',
        signals: { $onButtonClicked: '$onButtonClicked' },
        zIndex: constants.ui.zIndex.modalLayer + 1,
      },
      zIndex: constants.ui.zIndex.modalLayer,
    };
  }

  override _focus() {
    // Must fire announcer refresh or TTS doesn't run on ErrorModal re-focus
    if (ErrorModal._retryCount > 0) {
      this.fireAncestors(
        '$announcerRefresh',
        getFocusDepth(this.application, this),
      );
    }
  }

  override _unfocus() {
    this._setState('');
  }

  override _getFocused() {
    return this._Button;
  }

  override _handleBack() {
    const history = Router.getHistory();
    if (history.length) {
      Router.back();
      AppData?.modalService.close();
    } else {
      // Close the app if we can't go back
      closeApp();
    }
    this.resetRetryCount();
    return true;
  }

  override _handleKey() {
    // Do not allow the user to bypass the error modal
    return true;
  }

  hide() {
    this.patch({ alpha: 0 });
  }

  show() {
    this.patch({ alpha: 1 });
  }

  update(args: ErrorModalArgs) {
    const { cause, navigationRequest } = args;
    let finalCause = ErrorCause.UNKNOWN;
    let isResolvable = false;

    if (navigationRequest) {
      // Assume it was a navigation error if we received a navigation request
      if (cause) {
        finalCause = cause;
      } else if (navigationRequest.hash.includes(RoutePath.LIVE_EVENT_DETAIL)) {
        finalCause = ErrorCause.LIVE_PAGE;
      } else {
        finalCause = ErrorCause.NAVIGATION;
      }
      isResolvable = true;
    } else if (cause) {
      finalCause = cause;

      if (cause === ErrorCause.SPLASH) {
        isResolvable = true;
      } else {
        // Fatal, error cause is:
        // - CHANNEL: Never resolvable
        // - PLAYBACK: Never resolvable
        // - CONFIGURATION: Missing navigation request
        // - NAVIGATION: Missing navigation request
        // - UNKNOWN: No leads
        isResolvable = false;
      }
    } else {
      // Neither cause or request reported, unable to do anything
    }

    this.updateRetryCount(finalCause, navigationRequest);
    ErrorModal._cause = finalCause;
    ErrorModal._navigationRequest = navigationRequest;

    // In case of multiple update() calls, we need to reset the state
    // so it can run $enter() again for Resolvable and Fatal
    this._setState('');
    if (isResolvable && this.canRetry()) {
      this._setState('Resolvable');
    } else if (
      finalCause === ErrorCause.LIVE_ENDED_ON_EXIT_PLAYER ||
      finalCause === ErrorCause.LIVE_ENDED_ON_DEEPLINK
    ) {
      this._setState('LiveEnd');
    } else {
      this._setState('Fatal');
    }
  }

  private updateRetryCount(cause: ErrorCause, navigationRequest?: Request) {
    if (
      ErrorModal._cause === cause &&
      ErrorModal._navigationRequest?.hash === navigationRequest?.hash
    ) {
      ErrorModal._retryCount += 1;
    } else {
      this.resetRetryCount();
    }
  }

  private canRetry() {
    return ErrorModal._retryCount < constants.errors.maxRetry;
  }

  private resetRetryCount() {
    ErrorModal._retryCount = 0;
  }

  private retryNavigationRequest(navigationRequest: Request) {
    const { url, register } = navigationRequest;

    const params: Record<string, unknown> = {};
    register.forEach((value, key) => {
      params[key] = value;
    });

    Router.navigate(url, params);
    Router.reload();
    AppData?.modalService.close();
  }

  private setErrorModalStrings(stringPath: string) {
    const title = translate(`${stringPath}.title`);
    const description = translate(`${stringPath}.description`);
    const button = translate(`${stringPath}.button`);

    this._Title.patch({ text: title });
    this._Description.patch({ visible: !!description, text: description });
    this._Button.patch({ label: button });
  }

  private setPlaybackErrorModalStrings() {
    const stringPath =
      ErrorModal._cause === ErrorCause.LIVE_PLAYBACK
        ? 'error.fatal.livePlayback'
        : 'error.fatal.playback';
    const title = translate(`${stringPath}.title`);
    const button = translate(`${stringPath}.button`);

    this._Title.patch({ text: title });
    // No description for playback error
    this._Description.patch({ visible: false });
    this._Button.patch({ label: button });
  }

  private setUnavailableErrorModalStrings() {
    const stringPath = 'error.fatal.unavailable';
    const title = translate(`${stringPath}.title`);
    const button = translate(`${stringPath}.button`);

    this._Title.patch({ text: title });
    // No description for unavailable error
    this._Description.patch({ visible: false });
    this._Button.patch({ label: button });
  }

  // Implemented in states
  $onButtonClicked() {}

  static override _states() {
    return [
      class Resolvable extends this {
        override $enter() {
          const cause = ErrorModal._cause;

          // Only resolvable error causes are CONFIGURATION, SPLASH, and NAVIGATION
          switch (cause) {
            case ErrorCause.CONFIGURATION:
            case ErrorCause.SPLASH:
              return this.setErrorModalStrings(
                'error.resolvable.configuration',
              );
            case ErrorCause.NAVIGATION:
            case ErrorCause.LIVE_PAGE:
              return this.setErrorModalStrings('error.resolvable.navigation');
            default:
              return this._setState('Fatal');
          }
        }

        override $onButtonClicked() {
          const navigationRequest = ErrorModal._navigationRequest;
          if (navigationRequest) {
            this.retryNavigationRequest(navigationRequest);
          } else {
            // Assume there was an error in the splash page
            Router.navigate(RoutePath.SPLASH);
            Router.reload();
            AppData?.modalService.close();
          }
        }
      },
      class Fatal extends this {
        override $enter() {
          const cause = ErrorModal._cause;
          switch (cause) {
            case ErrorCause.PLAYBACK:
            case ErrorCause.LIVE_PLAYBACK:
              return this._setState('Fatal.Playback');
            case ErrorCause.CHANNEL:
              return this._setState('Fatal.Channel');
            case ErrorCause.LIVE_PAGE:
              return this._setState('Fatal.LivePage');
            case ErrorCause.UNAVAILABLE:
              return this._setState('Fatal.Unavailable');
            default:
              return this._setState('Fatal.Generic');
          }
        }

        static override _states() {
          return [
            // Channel errors are a special case: EPG page should have its own
            // handler for errors since we don't want to leave the page when a
            // channel's playback fails
            class Channel extends this {
              override $enter() {
                this.setPlaybackErrorModalStrings();
              }

              override _handleBack() {
                AppData?.modalService.close();
                return true;
              }

              override $onButtonClicked() {
                AppData?.modalService.close();
              }
            },
            class Playback extends this {
              override $enter() {
                this.setPlaybackErrorModalStrings();
              }

              override $onButtonClicked() {
                this._handleBack();
              }
            },

            class LivePage extends this {
              override $enter() {
                this.setErrorModalStrings('error.fatal.livePage');
              }

              override $onButtonClicked() {
                navigateHome();
              }
            },

            class Unavailable extends this {
              override $enter() {
                this.setUnavailableErrorModalStrings();
              }

              override $onButtonClicked() {
                this._handleBack();
              }
            },

            class Generic extends this {
              override $enter() {
                this.setErrorModalStrings('error.fatal.generic');
              }

              override _handleBack() {
                // Unable to leave by pressing back
                return true;
              }

              override $onButtonClicked() {
                closeApp();
              }
            },
          ];
        }
      },
      class LiveEnd extends this {
        override $enter() {
          const cause = ErrorModal._cause;
          switch (cause) {
            case ErrorCause.LIVE_ENDED_ON_EXIT_PLAYER:
              return this._setState('LiveEnd.OnExitPlayer');
            case ErrorCause.LIVE_ENDED_ON_DEEPLINK:
              return this._setState('LiveEnd.OnDeeplink');
          }
        }

        static override _states() {
          return [
            class OnExitPlayer extends this {
              override $enter() {
                this.setErrorModalStrings('error.liveEnd.onExitPlayer');
              }

              override $onButtonClicked() {
                this._handleBack();
              }

              override _handleBack() {
                // Do not navigate back to the detail page
                const history = Router.getHistory();

                // Remove LiveEventDetail from the history
                const filteredHistory = history.filter(
                  (entry: Router.HistoryEntry) =>
                    !entry.hash.includes(RoutePath.LIVE_EVENT_DETAIL),
                );
                Router.setHistory(filteredHistory);

                if (filteredHistory.length > 0) {
                  Router.back();
                } else {
                  navigateHome();
                }

                AppData?.modalService.close();
                return true;
              }
            },
            class OnDeeplink extends this {
              override $enter() {
                this.setErrorModalStrings('error.liveEnd.onDeeplink');
              }

              override $onButtonClicked() {
                this._handleBack();
              }

              override _handleBack() {
                navigateHome();
                return true;
              }
            },
          ];
        }
      },
    ];
  }
}
